import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { NumberField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-giveaway-picker")!

export const Route = createFileRoute("/tools/instagram-giveaway-picker")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-giveaway-picker",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-giveaway-picker" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-giveaway-picker" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-giveaway-picker" },
      ]}),
      createHowToSchema({ name: "How to Pick Instagram Giveaway Winners", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: GiveawayPickerPage,
})

function pickWinners(entries: string[], count: number, removeDuplicates: boolean): string[] {
  let pool = entries.map((e) => e.replace(/^@/, "").trim()).filter(Boolean)
  if (removeDuplicates) pool = [...new Set(pool.map((p) => p.toLowerCase()))]

  const winners: string[] = []
  const remaining = [...pool]
  for (let i = 0; i < Math.min(count, remaining.length); i++) {
    const randomBytes = new Uint32Array(1)
    crypto.getRandomValues(randomBytes)
    const index = randomBytes[0] % remaining.length
    winners.push(remaining.splice(index, 1)[0])
  }
  return winners
}

function GiveawayPickerPage() {
  const [entriesText, setEntriesText] = useState("")
  const [winnerCount, setWinnerCount] = useState("1")
  const [removeDuplicates, setRemoveDuplicates] = useState(true)
  const [winners, setWinners] = useState<string[] | null>(null)
  const [copied, setCopied] = useState(false)
  const [isRevealing, setIsRevealing] = useState(false)

  const entries = entriesText.split("\n").map((e) => e.replace(/^@/, "").trim()).filter(Boolean)
  const uniqueEntries = [...new Set(entries.map((e) => e.toLowerCase()))]
  const totalEntries = entries.length
  const uniqueCount = uniqueEntries.length
  const duplicateCount = totalEntries - uniqueCount

  const handlePick = () => {
    const count = Number(winnerCount) || 1
    if (entries.length === 0) return

    setIsRevealing(true)
    setWinners(null)

    // Brief delay for reveal effect
    setTimeout(() => {
      const result = pickWinners(entries, count, removeDuplicates)
      setWinners(result)
      setIsRevealing(false)
    }, 600)
  }

  const handlePickAgain = () => {
    setCopied(false)
    setIsRevealing(true)
    setWinners(null)

    setTimeout(() => {
      const count = Number(winnerCount) || 1
      const result = pickWinners(entries, count, removeDuplicates)
      setWinners(result)
      setIsRevealing(false)
    }, 600)
  }

  const copyWinners = async () => {
    if (!winners) return
    const text = winners.map((w) => `@${w}`).join("\n")
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const input = document.createElement("textarea")
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const reset = () => {
    setEntriesText("")
    setWinnerCount("1")
    setRemoveDuplicates(true)
    setWinners(null)
    setCopied(false)
    setIsRevealing(false)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>How the Giveaway Picker Works</SectionHeading>
            <Paragraph className="mb-4">
              This giveaway picker uses the Web Crypto API — specifically <code className="text-brand-button">crypto.getRandomValues()</code> — to generate cryptographically secure random numbers for winner selection. This is the same class of randomness used in encryption, secure tokens, and financial systems. Unlike <code className="text-brand-button">Math.random()</code>, which relies on a pseudo-random number generator (PRNG) with predictable patterns, cryptographic randomness is sourced directly from your operating system's entropy pool — a reservoir of unpredictable data gathered from hardware events like mouse movements, keystroke timing, and disk activity.
            </Paragraph>
            <Paragraph className="mb-4">
              Why does this matter for a giveaway? Fairness. With <code className="text-brand-button">Math.random()</code>, a technically sophisticated person could theoretically predict outcomes if they knew the seed value. With cryptographic randomness, prediction is computationally impossible. Every participant in your giveaway has a mathematically equal chance of being selected, and no one — including the person running the tool — can influence or predict the outcome.
            </Paragraph>
            <FeatureGrid items={[
              { icon: "🔐", title: "Cryptographic Randomness", description: "The selection process generates a random 32-bit unsigned integer for each pick using the Web Crypto API, then uses modular arithmetic to map that number to a participant index. This is the same randomness standard used in encryption and financial systems." },
              { icon: "🔄", title: "Fair Multi-Pick", description: "Selected winners are removed from the pool before subsequent picks, ensuring the same person cannot be chosen twice. The pick order determines tier placement for giveaways with ranked prizes." },
              { icon: "🔒", title: "100% Local Processing", description: "The entire operation runs locally in your browser — no data is sent to any server, stored in any database, or logged anywhere. You can verify this by monitoring the network tab in your browser's developer tools." },
              { icon: "👥", title: "Duplicate Removal", description: "Enable the duplicate removal option to ensure each unique username only gets one entry. This prevents users who commented multiple times from having a disproportionate chance of winning." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Running a Fair Instagram Giveaway</SectionHeading>
            <FeatureGrid items={[
              { icon: "📋", title: "Clear Rules", description: "Before your giveaway goes live, define every detail in your post caption: the entry method (comment, tag, follow), the deadline, eligibility requirements (age, location, account type), and how the winner will be announced. Ambiguous rules lead to disputes and erode trust with your audience." },
              { icon: "📹", title: "Public Selection", description: "Record your screen while you use this tool to pick the winner. Share the recording in your Instagram Stories or as a Reel. Showing the actual selection process proves that the giveaway was not rigged and that you did not simply hand the prize to a friend. Transparency builds loyalty and encourages more people to enter future giveaways." },
              { icon: "🎯", title: "Multi-Winner Fairness", description: "When selecting multiple winners, this tool removes each winner from the pool before picking the next one. This guarantees that the same person cannot win twice. If you are running a giveaway with tiered prizes (first place, second place, etc.), the pick order determines the tier — the first name selected gets the top prize." },
              { icon: "🤖", title: "Bot Filtering", description: "Before pasting your participant list, review it for obvious bot accounts — profiles with no posts, no profile picture, or usernames that are random strings of characters. Removing these manually before running the picker ensures your prize goes to a real person who genuinely engaged with your content." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Instagram Giveaway Rules and Compliance</SectionHeading>
            <Paragraph className="mb-4">
              Instagram has specific promotion guidelines that every giveaway must follow. Your post must include official rules, eligibility requirements, and a clear acknowledgment that the promotion is not sponsored, endorsed, administered by, or associated with Instagram. This disclaimer is not optional — failing to include it can result in your post being removed or your account being flagged.
            </Paragraph>
            <Paragraph className="mb-4">
              If your giveaway involves a brand partnership or sponsorship, FTC disclosure requirements apply. You must clearly disclose the relationship using language like "Sponsored" or "#ad" in a prominent position — not buried at the end of a long caption. The FTC takes these requirements seriously, and non-compliance can result in fines for both the creator and the brand.
            </Paragraph>
            <Paragraph className="mb-4">
              Consider age restrictions carefully. If your prize includes age-restricted items (alcohol, certain supplements, or 18+ products), you must state the minimum age for entry in your rules. Similarly, be mindful of geographic limitations — if you can only ship the prize to certain countries, say so upfront. Nothing damages your reputation faster than picking a winner who cannot actually receive the prize.
            </Paragraph>
            <Paragraph>
              Finally, Instagram explicitly prohibits requiring users to tag themselves in photos they are not actually in, or encouraging inaccurate tagging of any kind. Keep your entry requirements straightforward: comment, follow, tag a friend in the comments, or share to their Story. These are all compliant methods that drive genuine engagement.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Giveaway Ideas That Drive Engagement</SectionHeading>
            <TipList tips={[
              { title: "Comment-to-Enter", description: "The simplest and most effective format. Ask followers to comment a specific word, answer a question, or share their opinion. This drives comment volume, which boosts your post in the algorithm. The more comments, the wider the reach — creating a snowball effect where engagement drives visibility drives more engagement." },
              { title: "Tag-a-Friend", description: "Require participants to tag one or more friends in the comments. Each tag exposes your profile to a new potential follower. This is one of the most reliable ways to grow your audience organically, because the recommendation comes from someone the person already trusts." },
              { title: "Share-to-Story", description: "Ask entrants to share your giveaway post to their Instagram Story and tag your account. This puts your content in front of their entire follower base, dramatically expanding your reach beyond your existing audience. Use a branded hashtag so you can track shares." },
              { title: "Follow + Comment Combo", description: "Combine a follow requirement with a comment entry. This ensures that participants become followers who will see your future content, not just one-time visitors. It builds a lasting audience rather than generating a spike of temporary attention." },
              { title: "UGC Contests", description: "Ask followers to create their own content (a photo, video, or design) using your product and tag your account. This generates authentic user-generated content you can reshare, builds community, and gives participants a deeper connection to your brand than a simple comment ever could." },
              { title: "Collab Giveaways with Other Creators", description: "Partner with another creator or brand in your niche to offer a joint prize. Both accounts promote the giveaway, exposing each audience to the other. This is one of the fastest ways to gain qualified followers who are already interested in your content category." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>After the Giveaway — Retaining New Followers</SectionHeading>
            <TipList tips={[
              { title: "Do not change your content dramatically", description: "New followers came to your account because of the content they saw during the giveaway period. If you suddenly pivot to a completely different topic or style, they will unfollow. Stay consistent with the content that attracted them in the first place, and introduce new themes gradually." },
              { title: "Engage with new followers", description: "In the days after the giveaway, actively respond to comments, reply to Stories from new followers, and like their posts. This signals to the algorithm that you have a genuine relationship with these accounts, which helps your future content appear in their feeds rather than getting buried." },
              { title: "Follow up with a thank-you post", description: "Publish a post or Story thanking everyone who entered, announcing the winner, and sharing the total number of entries. This closes the loop on the giveaway, makes non-winners feel acknowledged, and reinforces that your giveaways are legitimate and worth entering next time." },
              { title: "Plan your next giveaway to maintain momentum", description: "The biggest drop-off in followers happens in the week after a giveaway ends. Combat this by teasing your next giveaway or offering an exclusive perk for new followers (a discount code, free resource, or early access). Give people a reason to stay beyond the prize they did not win." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!winners && !isRevealing ? (
            <div className="space-y-4">
              {/* Username textarea */}
              <div>
                <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">
                  Participant Usernames <span className="text-red-500 ml-0.5">*</span>
                </label>
                <textarea
                  value={entriesText}
                  onChange={(e) => setEntriesText(e.target.value)}
                  placeholder={"@user1\n@user2\n@user3\nuser4\nuser5"}
                  rows={8}
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-neutral-900 dark:text-white text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-button/40 focus:border-brand-button/40 transition-all resize-y font-mono"
                />
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">One username per line (@ is optional)</p>
              </div>

              {/* Winner count & duplicates */}
              <div className="grid grid-cols-2 gap-4">
                <NumberField
                  label="Number of Winners"
                  value={winnerCount}
                  onChange={setWinnerCount}
                  placeholder="1"
                  min={1}
                  max={100}
                  required
                />
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={removeDuplicates}
                      onChange={(e) => setRemoveDuplicates(e.target.checked)}
                      className="w-4.5 h-4.5 rounded border-neutral-300 dark:border-neutral-600 text-brand-button focus:ring-brand-button/40 accent-brand-accent"
                    />
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Remove duplicates</span>
                  </label>
                </div>
              </div>

              {/* Stats row */}
              {totalEntries > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">{totalEntries}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Total Entries</p>
                  </div>
                  <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">{uniqueCount}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Unique Entries</p>
                  </div>
                  <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">{duplicateCount}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Duplicates</p>
                  </div>
                </div>
              )}

              <SubmitButton onClick={handlePick} disabled={totalEntries === 0}>
                Pick {Number(winnerCount) > 1 ? `${winnerCount} Winners` : "Winner"}
              </SubmitButton>
            </div>
          ) : isRevealing ? (
            /* Reveal animation */
            <div className="py-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-accent animate-pulse mb-4">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-neutral-900 dark:text-white">Picking winners...</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Using cryptographic randomness</p>
            </div>
          ) : (
            /* Results */
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                  {winners!.length === 1 ? "Winner" : `${winners!.length} Winners`}
                </p>
                <div className="space-y-2">
                  {winners!.map((winner, i) => (
                    <div
                      key={`${winner}-${i}`}
                      className="rounded-xl bg-brand-accent/10 dark:from-brand-accent/20 dark:via-brand-accent/20 dark:to-brand-accent/20 border border-brand-accent/20 dark:border-brand-accent/30 p-4 flex items-center gap-3"
                    >
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-accent flex items-center justify-center text-white text-sm font-bold">
                        {i + 1}
                      </span>
                      <span className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">
                        @{winner}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats summary */}
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-3">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
                  Selected from {removeDuplicates ? uniqueCount : totalEntries} {removeDuplicates ? "unique " : ""}entries using cryptographic randomness
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={copyWinners}
                  className={`flex-1 h-12 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? "Copied!" : "Copy Winners"}
                </button>
                <button
                  onClick={handlePickAgain}
                  className="flex-1 h-12 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all"
                >
                  Pick Again
                </button>
              </div>

              <ResetButton onClick={reset}>Start Over</ResetButton>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
